/**
 * Minimal CSV helpers for exporting table data.
 */

export interface CsvColumn<T> {
  /** Column header as it appears in the first row of the file */
  header: string;
  /** Value for a given row - anything non-string is stringified */
  value: (row: T) => string | number | null | undefined;
}

/**
 * Escape a single CSV field.
 *
 * Values are always quoted so separators, quotes and newlines inside the data
 * cannot break the file. A leading =, +, - or @ is prefixed with a single quote
 * so spreadsheet applications treat the value as text rather than a formula.
 */
function escapeField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '""';

  let text = String(value);
  if (/^[=+\-@]/.test(text)) {
    text = `'${text}`;
  }

  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * Build a CSV document from rows and column definitions.
 * Uses CRLF line endings, which is what Excel expects.
 */
export function toCsv<T>(rows: T[], columns: Array<CsvColumn<T>>): string {
  const header = columns.map((column) => escapeField(column.header)).join(',');
  const body = rows.map((row) =>
    columns.map((column) => escapeField(column.value(row))).join(',')
  );

  return [header, ...body].join('\r\n');
}

/**
 * Trigger a browser download for the given CSV content.
 * The BOM makes Excel open UTF-8 files with the correct encoding.
 */
export function downloadCsv(fileName: string, csv: string): void {
  const BOM = '\uFEFF';
  const blob = new Blob([`${BOM}${csv}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}
